import { ControlledRadioGroup } from "@hm/fundamental"
import { useCallback } from "react"

import type {
	ControlledFilterConfig,
	ControlledFilterFormProps,
} from "../model/types"
import { ControlledDatepicker } from "./controlled-datapicker"
import { ControlledMultiselect } from "./controlled-multiselect"
import { ControlledSelect } from "./controlled-select"

const renderFilterItem = (
	config: ControlledFilterConfig,
	value: Record<string, unknown>,
	onFieldChange: (name: string, fieldValue: unknown) => void,
) => {
	switch (config.type) {
		case "select":
			return (
				<ControlledSelect
					key={config.name}
					name={config.name}
					label={config.label}
					options={config.values}
					value={value[config.name] as string | number | undefined}
					onChange={(val: string | number) =>
						onFieldChange(config.name, val)
					}
					data-cy={`filter-${config.name}`}
				/>
			)
		case "switch":
			return (
				<ControlledRadioGroup
					key={config.name}
					options={config.values.map(v => ({
						label: v.label,
						value: String(v.value),
					}))}
					value={String(
						(value[config.name] as string | number) ?? "",
					)}
					onChange={(val: string) => onFieldChange(config.name, val)}
					data-cy={`filter-${config.name}`}
				/>
			)
		case "multiselect":
			return (
				<ControlledMultiselect
					key={config.name}
					label={config.label}
					options={config.values}
					selectedValues={
						(value[config.name] as (string | number)[]) ?? []
					}
					onChange={(selected: (string | number)[]) =>
						onFieldChange(config.name, selected)
					}
					searchAriaLabel={config.searchAriaLabel}
					closeAriaLabel={config.closeAriaLabel}
					data-cy={`filter-${config.name}`}
				/>
			)
		case "datepicker":
			return (
				<ControlledDatepicker
					key={config.name}
					name={config.name}
					label={config.label}
					value={
						(value[config.name] as
							| Date
							| [Date | null, Date | null]
							| null) ?? null
					}
					onChange={val => onFieldChange(config.name, val)}
					showMonthYearPicker={config.showMonthYearPicker}
					language={config.language}
					monthsTabLabel={config.monthsTabLabel}
					daysTabLabel={config.daysTabLabel}
					selectRangeLabel={config.selectRangeLabel}
					data-cy={`filter-${config.name}`}
				/>
			)
		default:
			return null
	}
}

export const ControlledFilterForm = <
	T extends object = Record<string, unknown>,
>({
	value,
	config,
	onChange,
	"data-cy": dataCy = "filter-form",
	className,
}: ControlledFilterFormProps<T>): React.ReactElement => {
	const handleFieldChange = useCallback(
		(name: string, fieldValue: unknown) => {
			onChange({ ...value, [name]: fieldValue })
		},
		[value, onChange],
	)

	return (
		<fieldset className={className} data-cy={dataCy} aria-label="Filters">
			{config.map(item =>
				renderFilterItem(
					item,
					value as Record<string, unknown>,
					handleFieldChange,
				),
			)}
		</fieldset>
	)
}
